import { ContextPrimaryClient } from '@/modules/household/contexts/householdDetails.context';
import { AfterDeath } from '@/types/schema';
import { getPulidKind, PulidKind } from '@/utils/pulid';

export interface IsTileSelectableArgs {
  pulid: string;
  afterDeath: AfterDeath;
  primaryClients: ContextPrimaryClient[];
  isTwoClientHousehold: boolean;
  isOnDispositionsFormDiagram: boolean;
}

/**
 * Determine if a tile should be selectable (and thus clickable) in the diagram.
 * Restrictions only apply when actively authoring dispositions inside the
 * dispositive provisions form — not when viewing/editing the dispositions
 * diagram from the entity edit trowser, where every tile must remain
 * editable.
 */
export function isTileSelectable({
  pulid,
  afterDeath,
  primaryClients,
  isTwoClientHousehold,
  isOnDispositionsFormDiagram,
}: IsTileSelectableArgs): boolean {
  if (!isOnDispositionsFormDiagram) return true;

  const pulidKind = getPulidKind(pulid);

  // Organizations never support dispositions
  if (pulidKind === PulidKind.ClientOrganization) return false;

  // Non-client individuals never support dispositions
  if (
    pulidKind === PulidKind.ClientProfile &&
    !primaryClients?.some((c) => c.id === pulid)
  ) {
    return false;
  }

  // Final-death section does not support adding dispositions
  const isFinalDeathUnsupported = (() => {
    if (!afterDeath) return false;
    if (!isTwoClientHousehold) {
      return afterDeath === AfterDeath.First;
    }
    return afterDeath === AfterDeath.Second;
  })();

  if (isFinalDeathUnsupported) return false;

  return true;
}
